/**
 * SSOT for the default context ID used by the shell when no context is
 * selected, when the stored context is invalid, or when the backend returns
 * no contexts. MFEs that need to reason about the default context should
 * import this constant rather than hardcoding the literal.
 */
export const DEFAULT_CONTEXT_ID = 'central';

/**
 * Icon descriptor for a named context.
 * Discriminated by `kind` so consumers must handle each variant explicitly.
 * `fa` and `lucide` values are icon names; `svg` carries inline markup that
 * MUST use `currentColor` for tinting (enforced by bloom R6 validation).
 */
export type ContextIcon =
  | { kind: 'fa'; value: string }
  | { kind: 'lucide'; value: string }
  | { kind: 'svg'; value: string };

/**
 * A named application context in the shell (e.g. "ABAX Core", "SmartSite").
 * Contexts partition the shell's MFE surface into independent product areas.
 */
export interface AppContext {
  id: string;
  titleKey: string;
  icon: ContextIcon;
  accentColor: string;
  defaultRoute: string;
  order: number;
}

/**
 * Context assignment for a FederatedPackage.
 * When present, the package's MFEs activate only within this context.
 * When null, the package is context-agnostic (legacy behavior).
 */
export interface RemoteContext {
  contextId: string;
}

// --- Federation types with context support (PLT-02, PLT-03) ---

/**
 * Semantic version of a federated package. L1: previously declared
 * privately here AND duplicated in shell's
 * `src/types/internal-contracts/federated-package.d.ts`. Exported now so
 * the shell re-exports the platform SSOT instead.
 */
export type AppVersion = {
  major: number;
  minor: number;
  patch: number;
  prerelease?: string;
};

export interface RemoteApp {
  module: string;
  friendlyName: string;
  basePath: string;
  namedRoutes?: Record<string, string>;
}

export interface RemoteSidecar {
  module: string;
  friendlyName: string;
}

export interface RemoteParcel {
  module: string;
  friendlyName: string;
}

/**
 * A federated MFE package loaded by the shell.
 * The `context` field (PLT-02) associates the package with a specific context.
 * When null, the package is context-agnostic (legacy behavior).
 */
export interface FederatedPackage {
  id: string;
  entryUrl: string;
  app: RemoteApp | null;
  sidecar: RemoteSidecar | null;
  parcels: RemoteParcel[] | null;
  version: AppVersion;
  context: RemoteContext | null;
}

/**
 * A RemoteApp enriched with runtime activation metadata.
 * The `contextId` field (PLT-03) associates the app with a specific context
 * for activeWhen guard evaluation.
 */
export type ActivatableRemoteApp = RemoteApp & {
  federatedPackageId: string;
  federatedPackageEntryUrl: string;
  contextId?: string;
  isActive: (location: Location) => boolean;
};

/**
 * Discriminated outcome of `ContextApi.switchContext`. Replaces the prior
 * `Promise<void>` so MFEs can distinguish between "switch succeeded but the
 * user was already on that context", "another switch is in progress",
 * "the requested context is not in the user's available list", and
 * "fetch / commit failed". The `error` payload is constrained to the
 * fetch-failure variant — other failures self-describe via `reason`.
 */
export type SwitchContextResult =
  | { success: true; reason?: 'already-active' }
  | { success: false; reason: 'switch-in-progress' }
  | { success: false; reason: 'fetch-failed'; error: string }
  | { success: false; reason: 'context-unavailable' };

/**
 * Shell Frontend API — context namespace.
 * Exposed to MFEs as `ShellFrontendAPI.context.*`
 */
export interface ContextApi {
  getCurrentContext(): AppContext | undefined;
  getAvailableContexts(): AppContext[];
  switchContext(contextId: string): Promise<SwitchContextResult>;
  onContextChanged(callback: (context: AppContext) => void): () => void;
  /**
   * The navigation arrangement resolved for this session, read-only. 'base' is today's
   * menu; any other value is an alternative arrangement (see the Rearrange Navigation
   * epic). Lets MFEs and Datadog RUM observe which arrangement a session is on.
   */
  getActiveArrangementId(): string;
}
